import { SectionLabel } from "../ui.js";
import { useEffect, useState } from "react";
import * as speech from "../speech.js";
import * as voice from "../voice.js";
import * as voiceLive from "../live-voice.js";
import * as crowd from "../crowd.js";
import * as mix from "../mix.js";
import * as filler from "../filler.js";
import type { Lang } from "../commentary.js";

/**
 * Commentary controls.
 *
 * This panel had drifted badly: it led with the device's own voices — Samantha,
 * Daniel, "download a Premium build" — from when those were what you heard.
 * They are not any more. Ball-by-ball is 294 recorded lines in English and 155
 * in Hinglish, and the device voice is only reached when a line contains a
 * player name or a score AND the live model is switched off.
 *
 * So it now leads with what actually plays, and the device voice appears only
 * where it is genuinely relevant, labelled as the fallback it is.
 */



export function VoiceBar({ lang, setLang, voiceOn, setVoiceOn, expanded = false }: {
  lang: Lang; setLang: (l: Lang) => void;
  voiceOn: boolean; setVoiceOn: (v: boolean) => void;
  /**
   * Settings shows everything at once; the scorer keeps it behind a button.
   *
   * Hiding the voice names two taps deep was wrong on a screen whose whole job
   * is settings — you had to turn commentary on before you could see what you
   * were turning on.
   */
  expanded?: boolean;
}) {
  const [open, setOpen] = useState(expanded);
  const [crowdOn, setCrowdOn] = useState(crowd.isOn());
  const [liveState, setLiveState] = useState<voiceLive.State>(voiceLive.getState());
  const [clips, setClips] = useState<number>(0);
  const [pack, setPack] = useState<string>(voice.getPack(lang));

  useEffect(() => voiceLive.onState(setLiveState), []);
  // Load the device voices anyway: the fallback still needs one, it just is
  // not something to choose between here.
  useEffect(() => { speech.loadVoices(); }, []);
  useEffect(() => {
    setPack(voice.getPack(lang));
    void voice.load(lang).then((m) => {
      setClips(m ? Object.values(m).reduce((a, v) => a + v.length, 0) : 0);
    });
  }, [lang]);

  const sample = lang === "hi"
    ? "Chauka! Cover ke beech se khubsurat timing ke saath."
    : "FOUR! Beautifully timed through the covers. That is a lovely shot.";

  const toggle = () => {
    if (!voiceOn) {
      voice.unlock();
      setVoiceOn(true);
      void voice.prime(lang);
      voice.say(sample, "ball", lang, { interrupt: true });
    } else {
      voice.stop();
      crowd.stop();
      setCrowdOn(false);
      setVoiceOn(false);
    }
  };

  return (
    <div className="mb-1.5">
      <div className="flex items-center gap-2">
        <SectionLabel>Commentary</SectionLabel>

        <button onClick={toggle} aria-label={voiceOn ? "Mute commentary" : "Read commentary aloud"}
          className={`ml-auto flex h-11 items-center gap-1.5 rounded-full border px-3.5
            ${voiceOn ? "border-amber bg-amber text-board" : "border-line text-faint"}`}>
          {voiceOn ? <SpeakerOn /> : <SpeakerOff />}
          <span className="font-mono text-label uppercase">{voiceOn ? "On" : "Play"}</span>
        </button>

        {voiceOn && (
          <button
            onClick={() => {
              if (crowdOn) { crowd.stop(); setCrowdOn(false); }
              else { crowd.prime(); crowd.start(); setCrowdOn(true); }
            }}
            aria-label={crowdOn ? "Mute the crowd" : "Crowd atmosphere"}
            className={`flex h-11 items-center gap-1.5 rounded-full border px-3.5
              ${crowdOn ? "border-crease text-crease" : "border-line text-faint"}`}>
            <CrowdIcon />
            <span className="font-mono text-label uppercase">Crowd</span>
          </button>
        )}

        {voiceOn && !expanded && (
          <button onClick={() => setOpen((v) => !v)} aria-label="Voice settings"
            className="flex h-11 items-center rounded-full border border-line px-3.5
                       font-mono text-label uppercase text-faint">
            Voice
          </button>
        )}

        <div className="flex overflow-hidden rounded-full border border-line">
          {(["en", "hi"] as const).map((l) => (
            <button key={l} onClick={() => setLang(l)}
              className={`min-h-[44px] px-3.5 font-mono text-label uppercase
                ${lang === l ? "bg-amber text-board" : "text-faint"}`}>
              {l === "en" ? "Eng" : "Hin"}
            </button>
          ))}
        </div>
      </div>

      {(open || expanded) && (
        <div className="mt-2 overflow-hidden rounded-card border border-line bg-panel">

          <Faders />

          <ChatterPicker />

          {/* What actually plays, first. */}
          <div className="border-b border-line p-3">
            <div className="flex items-baseline gap-2">
              <SectionLabel>
                Voice
              </SectionLabel>
              <span className="ml-auto font-mono text-meta text-faint">
                {clips ? `${clips} recorded lines each` : "loading…"}
              </span>
            </div>

            {/* Each pack is a complete second set of recordings — the clips are
                whole sentences, so nothing can be mixed between them. */}
            <div className="mt-2 grid grid-cols-2 gap-2">
              {voice.packsFor(lang).map((pk) => (
                <button key={pk.id}
                  onClick={() => {
                    voice.setPack(lang, pk.id);
                    setPack(pk.id);
                    // Unlock and play regardless of the On switch: choosing a
                    // voice you cannot hear is not a choice.
                    voice.unlock();
                    void voice.load(lang).then(() => {
                      voice.say(sample, "ball", lang, { interrupt: true });
                    });
                  }}
                  className={`rounded-card border px-3 py-3 text-left
                    ${pack === pk.id ? "border-amber bg-[#20180C] text-amber" : "border-line bg-raise"}`}>
                  <span className="block text-body font-semibold">{pk.label}</span>
                  <span className="mt-0.5 block font-mono text-label text-faint">{pk.note}</span>
                </button>
              ))}
            </div>

            <p className="mt-2 text-meta leading-relaxed text-faint">
              Every ball, wicket and situation is a recorded line, not the phone reading text.
            </p>
          </div>

          {/* The one thing clips cannot cover. */}
          <div className="border-b border-line p-3">
            <SectionLabel className="mb-1.5 block">Scores and player names</SectionLabel>
            {liveState === "ready" ? (
              <p className="text-caption leading-relaxed text-crease">
                Spoken in the same voice as everything else.
              </p>
            ) : liveState === "loading" ? (
              <p className="text-caption text-amber">Fetching the voice model…</p>
            ) : liveState === "failed" ? (
              <p className="text-caption text-leather">
                Could not start. These lines will use the phone's voice instead.
              </p>
            ) : (
              <>
                <p className="mb-2 text-meta leading-relaxed text-faint">
                  "End of over 4, Team Altaf are 48 for 2" changes every match, so it cannot be
                  recorded in advance. Right now the phone reads those, which sounds different
                  from the rest. This runs the same voice on your device instead.
                </p>
                <button onClick={() => { voiceLive.enable(); }}
                  className="min-h-[44px] w-full rounded-card border border-line bg-raise
                             font-mono text-meta uppercase tracking-[.13em] text-chalk">
                  Use one voice throughout · 82 MB
                </button>
              </>
            )}
          </div>

          {/* No voice picker here on purpose.
              These lines used to offer a choice of six phone voices — Samantha,
              Daniel, Rishi and so on — none of which is the commentary voice.
              Choosing between them changes almost nothing, while implying the
              commentary would improve. The real fix is the button above. */}
          {liveState !== "ready" && (
            <p className="px-3 py-2.5 text-meta leading-relaxed text-faint">
              Until then your phone reads those few lines in its own voice.
            </p>
          )}
        </div>
      )}
    </div>
  );
}


function CrowdIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round">
      <path d="M4 14v-3" /><path d="M8 17V8" /><path d="M12 20V5" />
      <path d="M16 17V8" /><path d="M20 14v-3" />
    </svg>
  );
}
function SpeakerOn() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      <path d="M15.5 8.5a5 5 0 0 1 0 7" />
      <path d="M18.5 5.5a9 9 0 0 1 0 13" />
    </svg>
  );
}
function SpeakerOff() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 5 6 9H2v6h4l5 4V5z" />
      <path d="m23 9-6 6M17 9l6 6" />
    </svg>
  );
}


/**
 * Three faders.
 *
 * Which of commentary, crowd and music should be loudest changes with the
 * moment — music carries between overs, commentary has to cut through it the
 * instant a wicket falls — so this is a mix the scorer rides rather than a
 * balance chosen once in code.
 *
 * Separate from the on/off switches above on purpose. Muting the crowd and
 * pulling it to zero look the same from outside but mean different things, and
 * folding them together means switching it back on restores it at a level
 * nobody picked.
 */
function Faders() {
  const [m, setM] = useState(mix.all());
  useEffect(() => mix.onChange(setM), []);

  const rows: { ch: mix.Channel; label: string }[] = [
    { ch: "commentary", label: "Commentary" },
    { ch: "crowd", label: "Crowd" },
    { ch: "music", label: "Music" },
  ];

  return (
    <div className="border-b border-line p-3">
      <div className="flex items-baseline gap-2">
        <SectionLabel>Levels</SectionLabel>
        {/* Was 36x15. Padding rather than a taller row: the label is small on
            purpose, but the thing you press should not be. */}
        <button onClick={() => mix.reset()}
          className="-my-2 ml-auto min-h-[44px] px-2 font-mono text-label uppercase
                     tracking-[.12em] text-faint hover:text-chalk">
          Reset
        </button>
      </div>

      <div className="mt-2 space-y-2.5">
        {rows.map(({ ch, label }) => (
          <div key={ch} className="flex items-center gap-3">
            <span className="w-[74px] shrink-0 text-caption">{label}</span>
            <input
              type="range" min={0} max={1} step={0.05} value={m[ch]}
              aria-label={`${label} volume`}
              onChange={(e) => mix.set(ch, Number(e.target.value))}
              className="h-11 min-w-0 flex-1 accent-amber"
            />
            {/* A number, not just a position. A scorer comparing two grounds
                wants to be able to write the setting down. */}
            <span className="w-[34px] shrink-0 text-right font-mono text-meta text-faint">
              {Math.round(m[ch] * 100)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}


/**
 * How much the app talks when nothing is happening.
 *
 * Between balls used to be silence, which reads as broken rather than quiet.
 * This decides how much of that gap gets filled — and it belongs next to the
 * volume levels, because the two questions a scorer actually has are "how loud"
 * and "how much".
 */
function ChatterPicker() {
  const [level, setLevel] = useState(filler.getChatter());
  useEffect(() => filler.onChatterChange(setLevel), []);
  const note = filler.CHATTER.find((c) => c.v === level)?.note ?? "";

  return (
    <div className="border-b border-line p-3">
      <SectionLabel>
        Between balls
      </SectionLabel>
      <div className="mt-2 flex gap-1.5">
        {filler.CHATTER.map((c) => (
          <button key={c.v} onClick={() => filler.setChatter(c.v)}
            aria-pressed={level === c.v}
            className={`min-h-[44px] flex-1 rounded-control border px-1 text-caption
              ${level === c.v ? "border-amber bg-[#20180C] text-amber" : "border-line bg-raise"}`}>
            {c.label}
          </button>
        ))}
      </div>
      <p className="mt-2 text-meta leading-relaxed text-faint">{note}</p>
    </div>
  );
}
